import React from 'react';
import {
  useAddress,
  useContract,
  useSigner,
  useSwitchChain
} from '@thirdweb-dev/react';
import { useAsyncFn } from 'react-use';
import { getActiveChain } from '@app/constants/chains';
import { CustomConnectWalletButton } from '@app/components/CustomConnectWalletButton';
import { SubmitButton } from '@app/components/SubmitButton';
import { showErrorToast, showSuccessToast } from '@app/components/Toast';
import { ExternalLink } from '@app/components/ExternalLink';
import { QueryKeys } from '@app/constants/queryKeys';
import { useQueryClient } from '@tanstack/react-query';
import { BigNumber, utils } from 'ethers';
import { useNftTokens } from '@app/hooks/positions/useNftTokens';
import { useConfig } from '@app/config';
import { useNetworkMismatch } from '@app/hooks/thirdweb/useNetworkMismatch';

import IchiProxyHelper from 'abis/ichi/IchiProxyHelper.json';
import {
  IDerivedLiquidityByIchiInfo,
  useLiquidityByIchiState
} from '@app/state/liquidityByIchiStore';
import { useAllowance } from '@app/hooks/token/useAllowance';
import { useApproval } from '@app/hooks/token/useApproval';
import { useSupportedChain } from '@app/hooks/other/useSupportedChain';

interface Props {
  mintInfo: IDerivedLiquidityByIchiInfo;
  onComplete?: (txHash?: string) => void;
}

export const DepositToIchiWithKpNFTButton = ({
  mintInfo,
  onComplete
}: Props) => {
  const account = useAddress();
  const queryClient = useQueryClient();
  const chain = useSupportedChain();
  const switchChain = useSwitchChain();
  const signer = useSigner();
  const config = useConfig();
  const isNetworkMismatch = useNetworkMismatch();

  const {
    actions: { setAmount }
  } = useLiquidityByIchiState();
  const { updateNftTokens, tokens } = useNftTokens(account);

  const { data: allowance } = useAllowance({
    token: mintInfo?.vaultDetails?.depositToken,
    spenderAddress: config?.ICHI.PROXY_HELPER
  });

  const isNeedAllowance =
    !allowance?.formattedValue || allowance.formattedValue < mintInfo.amount;

  const { mutate: approve, isLoading: isApproving } = useApproval(false);

  const { contract } = useContract(
    config?.ICHI.PROXY_HELPER,
    IchiProxyHelper.abi
  );

  const [{ loading: isAddingLiquidityLoading }, addLiquidity] =
    useAsyncFn(async () => {
      if (!contract || !mintInfo.vaultDetails?.vaultId) {
        return;
      }

      try {
        const _amount0 = utils.parseUnits(
          mintInfo.amount,
          mintInfo?.vaultDetails?.depositToken.decimals
        );

        const _amount1 = utils.parseUnits(
          '0',
          mintInfo?.vaultDetails?.pairedToken.decimals
        );

        const [token0Address, token1Address] = mintInfo.isSwappedPair
          ? [
              mintInfo?.vaultDetails?.pairedToken.contractAddress,
              mintInfo?.vaultDetails?.depositToken.contractAddress
            ]
          : [
              mintInfo?.vaultDetails?.depositToken.contractAddress,
              mintInfo?.vaultDetails?.pairedToken.contractAddress
            ];

        const [amount0, amount1] = mintInfo.isSwappedPair
          ? [_amount1, _amount0]
          : [_amount0, _amount1];

        const res = await contract.call('depositAndCreateNFTPosition', [
          account,
          mintInfo.vaultDetails?.vaultId,
          token0Address,
          token1Address,
          amount0,
          amount1,
          mintInfo.nftPoolAddress,
          +mintInfo.lockDuration * 3600 * 24 // lock duration
        ]);

        if (res?.reason) {
          showErrorToast('Transaction rejected');
        }

        const txHash = res?.receipt?.transactionHash;

        if (txHash) {
          setAmount('');

          await queryClient.invalidateQueries([
            QueryKeys.ELIGIBLE_TO_REFERRAL,
            account
          ]);

          await updateNftTokens([
            ...(tokens ?? []),
            mintInfo.vaultDetails?.vaultId!
          ]);

          showSuccessToast(
            `Transaction successful.`,
            <ExternalLink txHash={txHash} />
          );

          if (onComplete) {
            onComplete(txHash);
          }
        }
      } catch (e: any) {
        if (e?.reason === 'user rejected transaction') {
          return;
        }

        showErrorToast(`Transaction failed`);

        console.error(e.message);
      }
    }, [contract, account, queryClient, mintInfo]);

  if (!account) {
    return <CustomConnectWalletButton />;
  }

  if (isNetworkMismatch)
    return (
      <SubmitButton
        label="Switch chain"
        isLoading={false}
        variant={'destructive'}
        onClick={() => switchChain(getActiveChain().chainId)}
      />
    );

  if (isNeedAllowance) {
    return (
      <SubmitButton
        label={
          isApproving
            ? 'Processing...'
            : `Approve ${mintInfo.vaultDetails?.depositToken.symbol}`
        }
        isLoading={isApproving}
        disabled={isApproving}
        onClick={() =>
          approve({
            token: mintInfo.vaultDetails?.depositToken,
            spenderAddress: config?.ICHI.PROXY_HELPER,
            amount: mintInfo.amount,
            network: chain,
            signer
          })
        }
      />
    );
  }

  return (
    <SubmitButton
      isLoading={isAddingLiquidityLoading}
      disabled={false}
      onClick={() => addLiquidity && addLiquidity()}
      label={isAddingLiquidityLoading ? 'Loading...' : 'Create Position'}
    />
  );
};
